import { useSearchkit } from '@searchkit/client';
import { useCallback, useMemo } from 'react';
import {
    applyCreateDefaultsToRecord,
    buildCreateDefaultsParticipation,
    resolveCreateDefaultsFromViewContext,
    type CreateDefaultsFieldMetadata,
    type FacetForCreateDefaults
} from './createDefaultsFromViewContext';

export type UseCreateDefaultsFromViewContextParams = {
    /** Views opt in explicitly; when false the returned function is a no-op. */
    enabled?: boolean;
    fields: string[];
    facets?: FacetForCreateDefaults[];
    fieldMetadata?: CreateDefaultsFieldMetadata[];
};

export type ApplyCreateDefaults = (record: Record<string, unknown>) => Record<string, unknown>;

/**
 * Prefills a create draft from the active view filters.
 *
 * Returns a stable function to call from create paths only (new row, create form init).
 * Filters are read when the function runs, so the draft always reflects the current view.
 */
export function useCreateDefaultsFromViewContext({
    enabled = true,
    fields,
    facets,
    fieldMetadata
}: UseCreateDefaultsFromViewContextParams): ApplyCreateDefaults {
    const searchkit = useSearchkit();

    const participation = useMemo(() => {
        if (!enabled) {
            return [];
        }

        return buildCreateDefaultsParticipation({ fields, facets, fieldMetadata });
    }, [enabled, fields, facets, fieldMetadata]);

    const applyCreateDefaults = useCallback(
        (record: Record<string, unknown>) => {
            if (participation.length === 0 || typeof searchkit?.getFiltersByIdentifier !== 'function') {
                return record;
            }

            const defaults = resolveCreateDefaultsFromViewContext(
                (identifier) => searchkit.getFiltersByIdentifier(identifier) || [],
                participation
            );

            return applyCreateDefaultsToRecord(record, defaults);
        },
        [participation, searchkit]
    );

    return applyCreateDefaults;
}
